import { createElement } from "react"
import { Badge } from "@rhumbix/rmbx_design_system_web"
import { OverrideBadge } from "../../../toolbar/Toolbar/styles"
import PivotHeader from "../custom-headers/pivot-header"
import TimecardCellRenderer from "../cell-renderers/timecard-cell-renderer"
import DefaultCellRenderer from "../cell-renderers/default-cell-renderer"
import { pivotValueValueGetter } from "../../../common/ag-grid-value-getters"
import { workShiftDateDefaultTimeValueSetter } from "../../../common/ag-grid-value-setters"
import { canPasteIntoCell, getAvailableStatusValues } from "../../../common/ag-grid-utils"
import { lockedPeriodPivotCellClassRule } from "../../../common/ag-grid-ts-utils"
import { filterFormatter } from "../../../common/ag-grid-value-formatters"
import { statusesByUserRole } from "../../../common/constants"
import { compareGroupColumns, compareTimekeepingEntryObjects } from "../../../common/ag-grid-comparators"
import { pivotTotalColumnCustomTooltipMessageBuilder, pivotValueColumnCustomTooltipMessageBuilder } from "../../../common/ag-grid-custom-tooltip-formatters"
import {
    costCodeColDefCreator,
    dateColDefCreator,
    employeeColDefCreator,
    foremanColDefCreator,
    projectColDefCreator,
    stringColDefCreator,
    timeCardColDefCreator,
    timekeepingStatusColDefCreator,
} from "./standard-col-def-creators"
import {
    classificationFilterDef,
    customTkEntryStatusFilterDef,
    employeeFilterDef,
    laborTypeFilterDef,
    multiCohortFilterDef,
    multiExcludeCohortFilterDef,
    multiGroupFilterDef,
    multiProjectFilterDef,
    projectStatusFilterDef,
    timeCardOwnerFilterDef,
    tradeFilterDef,
    weekUpdatedNameFilterDef,
} from "../../../filters/filter-defs"
import { getFlagEnabled } from "../../../getFlagValue"
import { format, isValid } from "date-fns"
import { buildPTORecordsByEmployeeDate, computeCellPTOStatus, computePTOFulfillment } from "../../../selectors/pto"
import { getObjectId } from "../../../common/ag-grid-ts-utils"
import { getAsDate } from "../../../common/ts-utils"
import { STORED_DATE_ONLY_FORMAT } from "../../../common/constants"
import { loadAllEntities, loadEmployeeHolidays, loadEmployeePTO } from "../../../cached-data/actions"
import { resolveSignatureStateForDisplay, selectedCellHasAllSyncedData, selectedCellUnableToTransition } from "../utils"
import { getSignatureRequestTooltip, getSignatureTooltip, singleEmployeeIsSelected } from "../../../actions/timekeeping-signatures"
import { filterData, hasActiveFancySearch } from "../../../common/ag-grid-grouping-utils"
import { getTimekeepingEntryTable } from "./timekeeping-entries"
import { getAbsenceTable } from "./absences"
import { getShiftsAndBreaksTable } from "./shifts-and-breaks"
import { getPTORequestsTable } from "./pto-requests"
import { textFieldOptionCreator } from "../../../filters/filter-def-creators"
import { toggleException } from "../../../actions/exceptions"
import { activateException } from "../custom-filters/utils"
import { SHOW_EMPLOYEE_MODE, SHOW_EMPLOYEE_MODE_LABELS } from "../../../common/constants"
import { getCookie } from "../../../common/ts-utils"

const getFindConfigForCustomField = option => {
    const tkSources = ["timekeepingEntries", "absences", "employeeEntries", "ewsStartStopTimes"]
    const sourceField = option.model === "Employee" ? "/employee" : "/project"
    const resource = option.model === "Employee" ? "employees" : "projects"
    return {
        dropdownDisplayName: `${option.model} ${option.label}`,
        textInputPlaceholder: `Find a ${option.label}`,
        resources: tkSources.map(sourceType => ({
            sourceType,
            sourceField,
            reference: {
                resource,
                fields: [`/custom_fields/${option.name}`],
            },
        })),
    }
}

const allExceptionsToggle = context => {
    const exceptionTypes = context.currentUser.features.cico ? ["cicoDiffException", "workRulesException"] : ["workRulesException"]
    const allOn = exceptionTypes.every(et => context.exceptions[et].enabled)
    const allOff = exceptionTypes.every(et => !context.exceptions[et].enabled)
    const isMixed = !allOn && !allOff
    const desiredState = !!(isMixed || allOff)
    return dispatch => {
        if (context.currentUser.features.cico) {
            if (context.exceptions.cicoDiffException.enabled !== desiredState) dispatch(toggleException("cicoDiffException"))
        }
        if (context.exceptions.workRulesException.enabled !== desiredState) dispatch(toggleException("workRulesException"))
    }
}

// Cache the latest computePTOFulfillment and pre-grouped PTO/holiday index by
// reference equality on the underlying entity dicts. The fancy-search "PTO"
// find category invokes its customMatch once per visible cell, so recomputing
// either of these for each cell would be wasteful.
let _ptoFulfillmentCacheKey = null
let _ptoFulfillmentCacheValue = {}
const getMemoizedPTOFulfillment = (entities, exceptions) => {
    const ptoObjects = entities?.employeePTO?.objects ?? {}
    const holidayObjects = entities?.employeeHolidays?.objects ?? {}
    const tkSummary = exceptions?.tkSummary ?? null
    if (_ptoFulfillmentCacheKey && _ptoFulfillmentCacheKey[0] === ptoObjects && _ptoFulfillmentCacheKey[1] === holidayObjects && _ptoFulfillmentCacheKey[2] === tkSummary) {
        return _ptoFulfillmentCacheValue
    }
    _ptoFulfillmentCacheKey = [ptoObjects, holidayObjects, tkSummary]
    _ptoFulfillmentCacheValue = computePTOFulfillment(ptoObjects, holidayObjects, tkSummary)
    return _ptoFulfillmentCacheValue
}

let _ptoRecordsIndexCacheKey = null
let _ptoRecordsIndexCacheValue = new Map()
const getMemoizedPTORecordsByEmployeeDate = entities => {
    const ptoObjects = entities?.employeePTO?.objects ?? {}
    const holidayObjects = entities?.employeeHolidays?.objects ?? {}
    if (_ptoRecordsIndexCacheKey && _ptoRecordsIndexCacheKey[0] === ptoObjects && _ptoRecordsIndexCacheKey[1] === holidayObjects) {
        return _ptoRecordsIndexCacheValue
    }
    _ptoRecordsIndexCacheKey = [ptoObjects, holidayObjects]
    _ptoRecordsIndexCacheValue = buildPTORecordsByEmployeeDate(ptoObjects, holidayObjects)
    return _ptoRecordsIndexCacheValue
}

// Cache the latest employee+date index of EoD answer records by reference equality on
// `context.exceptions.eodAnswersByEmployee` (fetched once per view by ChecklistButton's
// doEodAnswersFetch effect -- see refreshEodAnswersByEmployee). The EoD findConfig categories'
// customMatch invokes this once per visible cell, so recomputing the index for each cell would
// be wasteful.
let _eodAnswersIndexCacheKey = null
let _eodAnswersIndexCacheValue = new Map()
const getMemoizedEodAnswersByEmployeeDate = eodAnswersByEmployee => {
    if (_eodAnswersIndexCacheKey === eodAnswersByEmployee) return _eodAnswersIndexCacheValue
    _eodAnswersIndexCacheKey = eodAnswersByEmployee
    const index = new Map()
    Object.entries(eodAnswersByEmployee ?? {}).forEach(([employeeId, records]) => {
        const employeeRecords = records ?? []
        employeeRecords.forEach(record => {
            const key = `${employeeId}_${record.date}`
            if (!index.has(key)) index.set(key, [])
            index.get(key).push(record)
        })
    })
    _eodAnswersIndexCacheValue = index
    return _eodAnswersIndexCacheValue
}

const getEodAnswersForCell = (eodAnswersByEmployee, employeeId, date) => {
    if (employeeId == null || !date) return []
    return getMemoizedEodAnswersByEmployeeDate(eodAnswersByEmployee).get(`${employeeId}_${date}`) ?? []
}

/**
 * Shared `customMatch` builder for the EoD-answer findConfig categories (WA-9511).
 *
 * EoD answers no longer ride on the entry itself -- they're fetched once per view into
 * `context.exceptions.eodAnswersByEmployee` (see `refreshEodAnswersByEmployee`) and joined here
 * by employee+date, the same way `buildPTOFindConfig` joins `context.exceptions.tkSummary`. Using
 * `customMatch` instead of a `resources`/`sourceField` list also means every leaf resource type
 * present in the cell (TK entries, shift extras, shifts & breaks, absences) is covered
 * automatically via the row-level `selectedCellData` path below, rather than only the leaf types
 * explicitly listed in a `resources` array.
 *
 * @param matchesAnswers (answers, term) => boolean -- given that day's EoD answer records and a
 *   single search term, whether they match. `term` is always a single value; array search terms
 *   (multi-select dropdown options) are checked one at a time.
 */
const buildEodCustomMatch = matchesAnswers => (params, selectedCellData, searchTerm) => {
    const { context, node, column } = params
    const terms = Array.isArray(searchTerm) ? searchTerm : [searchTerm]
    const eodAnswersByEmployee = context.exceptions?.eodAnswersByEmployee

    const matchesEmployeeDate = (employeeId, date) => {
        const answers = getEodAnswersForCell(eodAnswersByEmployee, employeeId, date)
        if (answers.length === 0) return false
        return terms.some(term => matchesAnswers(answers, term))
    }

    // Cell-level path (searchVisibleCells): infer employee/date from the row's grouping and the
    // pivot column, matching buildPTOFindConfig's approach.
    if (node && column) {
        let employeeNode = node
        while (employeeNode && employeeNode.field !== "/employee") employeeNode = employeeNode.parent
        const employeeId = employeeNode ? Number(employeeNode.key) : null
        const date = column.getColDef?.()?.pivotKeys?.[0] ?? column.colDef?.pivotKeys?.[0]
        return matchesEmployeeDate(employeeId, date)
    }

    // Row-level path (filterData "Filter results" checkbox): selectedCellData is
    // `{ [resourceType]: [record] }` across every leaf resource type in the cell.
    for (const [resource, records] of Object.entries(selectedCellData || {})) {
        // EoD answers currently only attach to TK entries -- skip the "employees" placeholder
        // resource (WA-8820) rather than trying to match it by date, since it has no answers.
        if (resource === "employees") continue
        for (const record of records || []) {
            if (record?.dummy) continue
            const employeeId = getObjectId(record?.employee) ?? null
            if (!employeeId) continue
            if (matchesEmployeeDate(employeeId, record?.date ?? null)) return true
        }
    }
    return false
}

/**
 * Build the "PTO" entry of the weekly-view findConfig. Gated by WA-9197-pto-accruals.
 *
 * Unlike sourceField-based categories, PTO state is a derived property of the cell:
 * it depends on (employee, date, optional cost code) joined against Redux PTO + holiday
 * records and the computed fulfillment map. We therefore expose it via the `customMatch`
 * escape hatch on findConfig instead of a `resources` list.
 *
 * The dropdown offers three options driving the match:
 *   - `unfulfilled`: any PTO/holiday request for this cell that is NOT fulfilled
 *   - `fulfilled`: every matching request is fulfilled (and at least one exists)
 *   - `any`: at least one matching request exists, regardless of fulfillment
 */
const buildPTOFindConfig = () => ({
    dropdownDisplayName: "PTO",
    autoFilterEmptyValues: true,
    options: [
        { label: "Unfulfilled", value: "unfulfilled" },
        { label: "Fulfilled", value: "fulfilled" },
        { label: "Any", value: "any" },
    ],
    customMatch: (params, selectedCellData, searchTerm) => {
        const { context, node, column } = params
        const ptoFulfillment = getMemoizedPTOFulfillment(context.entities, context.exceptions)
        const recordsByEmployeeDate = getMemoizedPTORecordsByEmployeeDate(context.entities)
        const employeePTO = context.entities?.employeePTO?.objects ?? {}
        const employeeHolidays = context.entities?.employeeHolidays?.objects ?? {}
        const costCodes = context.referenceableData?.costCodes

        const matchesTerm = ({ employeeId, date, costCodeId }) => {
            const { ptoRequested, ptoFulfilled } = computeCellPTOStatus({
                employeeId,
                date,
                costCodeId,
                recordsByEmployeeDate,
                ptoFulfillment,
                costCodes,
            })
            if (!ptoRequested) return false
            if (searchTerm === "fulfilled") return ptoFulfilled
            if (searchTerm === "any") return true
            // default + "unfulfilled"
            return !ptoFulfilled
        }

        // Cell-level path (searchVisibleCells): infer employee/date from the row's grouping
        // and the pivot column. Cost-code-grouped rows scope to the cost code's PTO bucket.
        if (node && column) {
            let employeeNode = node
            while (employeeNode && employeeNode.field !== "/employee") employeeNode = employeeNode.parent
            const employeeId = employeeNode ? Number(employeeNode.key) : null
            const date = column.getColDef?.()?.pivotKeys?.[0] ?? column.colDef?.pivotKeys?.[0]
            const costCodeId = node.field === "/cost_code" ? Number(node.key) : null
            return matchesTerm({ employeeId, date, costCodeId })
        }

        // Row-level path (filterData "Filter results" checkbox): selectedCellData is
        // `{ [resourceType]: [record] }`. Use the record's own employee/date.
        //
        // The "employees" resource (WA-8820-workers-with-no-time) is a per-week placeholder
        // for employees with no time data — so a single record represents the whole week, and
        // its `date` field is the week start, not a specific day. For those rows we widen the
        // check to "does this employee have any matching PTO/holiday in the filter range?"
        // so an unfulfilled-PTO filter retains exactly the employees that *should* show a
        // PTO request but have no timecard for it.
        // context.filters.startDate/endDate are Date objects; PTO records use YYYY-MM-DD
        // strings. Normalize the filter bounds so the range check is a pure string compare.
        const toDateStr = d => {
            if (!d) return null
            const asDate = getAsDate(d)
            return isValid(asDate) ? format(asDate, STORED_DATE_ONLY_FORMAT) : null
        }
        const startDate = toDateStr(context.filters?.startDate)
        const endDate = toDateStr(context.filters?.endDate)
        const dateInRange = d => (!startDate || d >= startDate) && (!endDate || d <= endDate)
        for (const [resource, records] of Object.entries(selectedCellData || {})) {
            for (const record of records || []) {
                if (record?.dummy) continue
                const employeeId = getObjectId(record?.employee) ?? null
                if (!employeeId) continue

                if (resource === "employees") {
                    const empRecords = [
                        ...Object.values(employeePTO).filter(r => r.employee === employeeId && dateInRange(r.date)),
                        ...Object.values(employeeHolidays).filter(r => r.employee === employeeId && dateInRange(r.date)),
                    ]
                    if (empRecords.length === 0) continue
                    if (searchTerm === "any") return true
                    const fulfilledStates = empRecords.map(r => ptoFulfillment[String(r.id)] === true)
                    // "fulfilled" keeps employees with at least one fulfilled PTO record;
                    // "unfulfilled" (default) keeps employees with at least one unfulfilled.
                    if (searchTerm === "fulfilled") {
                        if (fulfilledStates.some(Boolean)) return true
                    } else if (fulfilledStates.some(s => !s)) {
                        return true
                    }
                    continue
                }

                if (matchesTerm({ employeeId, date: record?.date ?? null, costCodeId: null })) return true
            }
        }
        return false
    },
})

/**
 * Build the "Signature" entry of the weekly-view findConfig.
 */
const buildSignatureFindConfig = () =>
    getFlagEnabled("WA-9467-signature-revalidation")
        ? {
              // `signature_state` is serialized as SIGNED / SIGNED_INVALID / MISSING. `MISSING` is further split into
              // `MISSING_REQUIRED`/`MISSING_NOT_REQUIRED` using `require_signature`.
              // Start/stop times don't have a `require_signature` field, so they only match on SIGNED and SIGNED_INVALID.
              dropdownDisplayName: "Signature",
              keepEmptyValues: true,
              options: [
                  { label: "No signature required", value: "MISSING_NOT_REQUIRED" },
                  { label: "Missing required signature", value: "MISSING_REQUIRED" },
                  { label: "Signed", value: "SIGNED" },
                  { label: "Signature invalidated", value: "SIGNED_INVALID" },
              ],
              resources: ["timekeepingEntries", "employeeEntries", "ewsStartStopTimes"].map(sourceType => ({
                  sourceType,
                  sourceField: entry => resolveSignatureStateForDisplay(entry.signature_state, entry.require_signature),
                  // Avoids "SIGNED" incorrectly matching "SIGNED_INVALID" via substring overlap
                  exactMatch: true,
              })),
          }
        : {
              // Filter on whether the raw `/signature` blob is present
              dropdownDisplayName: "Signature",
              keepEmptyValues: true,
              options: [
                  { label: "Unsigned", value: "__null__" },
                  { label: "Signed", value: "__not null__" },
              ],
              resources: [
                  {
                      sourceType: "timekeepingEntries",
                      sourceField: "/signature",
                  },
              ],
          }

/**
 * Build one findConfig entry per active company EoD question. Gated by
 * WA-9511-end-of-day-questions.
 *
 * `catalog` is the list of `{question_id, label, values}` fetched from
 * `/api_v4/eod_questions/fancy_find_options/`. Each entry becomes its own dropdown category
 * keyed `eodQuestion_<question_id>`, matching against that day's answers joined via
 * `buildEodCustomMatch` -- see that function's docstring for why this needs `customMatch`
 * instead of `resources`/`sourceField`.
 */
const buildEodQuestionFindConfigs = (catalog = []) =>
    catalog.reduce((configs, { question_id: questionId, label, values }) => {
        configs[`eodQuestion_${questionId}`] = {
            dropdownDisplayName: label,
            options: values.map(value => ({ label: value, value })),
            customMatch: buildEodCustomMatch((answers, term) => answers.some(answer => answer.question_id === questionId && answer.display_text === term)),
        }
        return configs
    }, {})

/**
 * Build the "End of Day Issue Answers" findConfig category. Gated by WA-9511-end-of-day-questions.
 *
 * Always offers the fixed "Any issue answer" shortcut (narrows to days with at least one
 * ISSUE-category EoD answer, regardless of which question), plus one option per catalog question
 * that has ever offered an ISSUE-category choice (`has_issue_category`), narrowing to days where
 * that specific question was answered with an issue. Non-issue-capable questions (e.g. a
 * free-text-only question) are excluded since "issue" can never apply to them.
 */
const buildEodIssueAnswersFindConfig = (catalog = []) => ({
    dropdownDisplayName: "End of Day Issue Answers",
    options: [
        { label: "Any issue answer", value: "ANY" },
        ...catalog.filter(({ has_issue_category: hasIssueCategory }) => hasIssueCategory).map(({ question_id: questionId, label }) => ({ label, value: `question_${questionId}` })),
    ],
    customMatch: buildEodCustomMatch((answers, term) => {
        const issueAnswers = answers.filter(answer => answer.category === "ISSUE")
        if (term === "ANY") return issueAnswers.length > 0
        const questionId = Number(String(term).replace("question_", ""))
        return issueAnswers.some(answer => answer.question_id === questionId)
    }),
})

/**
 * Build the free-text findConfig category for searching EoD answer comments. Gated by
 * WA-9511-end-of-day-questions.
 *
 * Matches arbitrary substrings against any answer's `follow_up_text` for that day, regardless of
 * `category` (issue or not) -- unlike `buildEodQuestionFindConfigs`/`buildEodIssueAnswersFindConfig`,
 * this has no `options` dropdown, so the search bar renders it as a plain text input.
 */
const buildEodCommentsFindConfig = () => ({
    dropdownDisplayName: "End of Day Comments",
    textInputPlaceholder: "Find text in an EoD answer comment",
    customMatch: buildEodCustomMatch((answers, term) => answers.some(answer => answer.follow_up_text && String(term) && answer.follow_up_text.toLowerCase().includes(String(term).toLowerCase()))),
})

/**
 * Get the set of weekly view filters. We allow an optional keyPrefix to be passed in so that we can
 * have a separate set of the same filters for timekeeping exports.
 * @param user
 * @param featureFlags
 * @returns {*[]}
 */
export const getWeeklyViewFilters = (user, featureFlags) => {
    const textFieldOptions = user && user.text_field_options ? user.text_field_options : []

    const employeeTextFieldOptionFilterDefs = textFieldOptions
        .filter(option => option.model === "Employee")
        .map(option => textFieldOptionCreator(`Employee ${option.label}`, option.model, option.name))

    const projectTextFieldOptionFilterDefs = textFieldOptions.filter(option => option.model === "Project").map(option => textFieldOptionCreator(`Project ${option.label}`, option.model, option.name))

    const cohortTextFieldOptionFilterDefs = textFieldOptions.filter(option => option.model === "Cohort").map(option => textFieldOptionCreator(`Cohort ${option.label}`, option.model, option.name))

    return [
        weekUpdatedNameFilterDef,
        multiGroupFilterDef,
        projectStatusFilterDef,
        multiProjectFilterDef,
        ...projectTextFieldOptionFilterDefs,
        timeCardOwnerFilterDef,
        tradeFilterDef,
        classificationFilterDef,
        ...(featureFlags.labor_type_filter_visible ? [laborTypeFilterDef] : []),
        employeeFilterDef,
        multiCohortFilterDef,
        multiExcludeCohortFilterDef,
        ...cohortTextFieldOptionFilterDefs,
        ...employeeTextFieldOptionFilterDefs,
        customTkEntryStatusFilterDef,
    ]
}

export function getWeeklyTimeCardSettings(featureFlags, currentProject, user, currentProjectIds, sourceData, config, entities) {
    const eodQuestionFancyFindOptions = entities?.eodQuestionFancyFindOptions ?? []
    const userRole = user ? user.user_role : undefined

    const textFieldOptions = user && user.text_field_options ? user.text_field_options : []

    const textFieldFindConfigs = Object.fromEntries(
        textFieldOptions.filter(option => ["Employee", "Project"].includes(option.model)).map(option => [`${option.model}_${option.name}`, getFindConfigForCustomField(option)])
    )

    const pivotFlex = { flex: 1 }
    const pivotTotalFlex = { flex: 1.5 }

    const filters = getWeeklyViewFilters(user, featureFlags)

    const groupByConfiguration = [
        {
            cols: ["/employee", "/cost_code"],
            label: "Employee",
        },
        {
            cols: ["/foreman", "/employee", "/cost_code"],
            label: "Time Card Owner",
        },
        {
            cols: ["/project", "/foreman", "/employee", "/cost_code"],
            label: "Project",
        },
        {
            cols: ["/classification", "/employee", "/cost_code"],
            label: "Classification",
        },
        {
            cols: ["/trade", "/employee", "/cost_code"],
            label: "Trade",
        },
        {
            cols: ["/status", "/foreman", "/employee", "/cost_code"],
            label: "Status",
        },
        {
            cols: ["/cost_code", "/employee"],
            label: "Cost Code",
        },
        {
            cols: ["/foreman", "/work_shift_id", "/employee", "/cost_code"],
            label: "Time Card",
        },
    ]

    // toggle on or shifts_and_breaks data exists
    const shouldShowShiftsAndBreaks = featureFlags.shifts_and_breaks || sourceData?.sourceData?.ewsStartStopTimes?.length

    const employeeSearchFields = ["/first_name", "/last_name", "/company_supplied_id", "concat(/first_name, /last_name)", "concat(/last_name, /first_name)"]

    const cicoResource = featureFlags.cico ? ["timelineEntryVersions"] : []
    const workShiftResource = ["workShifts"]
    const employeesLazyLoadResource = getFlagEnabled("WA-8820-workers-with-no-time") ? { lazyLoadResources: ["employees"] } : {}

    const cicoException = {
        label: "CI/CO",
        action: activateException(() => toggleException("cicoDiffException")),
        exceptionParams: ["cicoDiffException"],
        hasGear: true,
        gearModalType: "cicoException",
        isChild: true,
    }
    const workRulesException = {
        label: "Work Rules",
        action: activateException(() => toggleException("workRulesException")),
        exceptionParams: ["workRulesException"],
        hasGear: true,
        gearModalType: "workRulesException",
        isChild: true,
        badge: exceptions => {
            const overrides = exceptions?.workRulesException?.workRuleOverride
            return overrides?.length > 0 ? createElement(Badge, { text: "Override", width: 65, height: 20 }) : null
        },
    }
    const customTooltip = featureFlags.cico ? {} : { customTooltip: pivotValueColumnCustomTooltipMessageBuilder }
    const totalCustomTooltip = featureFlags.cico ? {} : { customTooltip: pivotTotalColumnCustomTooltipMessageBuilder }
    const exceptionFilters = ["cicoDiffException", "workRulesException"]
    // Single source of truth for the Copy Week button's disabled/tooltip precedence,
    // so `disabled` and `tooltip` can't drift out of sync with each other.
    const getCopyWeekButtonState = context => {
        const hasData = Object.values(sourceData?.sourceData || {}).some(arr => arr?.length > 0)
        if (!hasData) return { disabled: true, tooltip: "No data visible in the current week to copy" }
        if (hasActiveFancySearch(context)) return { disabled: true, tooltip: "Close Find before trying to copy a week" }
        return { disabled: false, tooltip: "Copy all visible time entries to another week" }
    }
    return {
        tableName: "Weekly Time Card",
        navId: "pivot",
        // TODO: rename this to sourceData? row data? rename sourceData to rowData??
        resources: ["timekeepingEntries", "absences", "employeeEntries", "ewsStartStopTimes", ...cicoResource, ...workShiftResource],
        ...employeesLazyLoadResource,
        filters,
        additionalQueryParams: { for_dashboard: true },
        ...(getFlagEnabled("WA-9197-pto-accruals")
            ? {
                  entityLoadActions: (dispatch, filters) => {
                      const params = {
                          shift_start_time_0: filters.shift_start_time_0,
                          shift_start_time_1: filters.shift_start_time_1,
                      }
                      dispatch(loadEmployeePTO(params))
                      dispatch(loadEmployeeHolidays(params))
                  },
              }
            : {}),
        forceRefIds: { employees: user ? [user.employee_id] : [] },
        referenceablesWithStringIds: ["timekeepingStatuses"],
        referenceableQueryParamFn: filters => ({
            employees: { cico_start_date: filters.shift_start_time_0, cico_end_date: filters.shift_start_time_1 },
        }),
        relatedNames: {
            timekeepingEntries: {
                companyClassifications: ["/modifier_active/classification"],
                companyTrades: ["/modifier_active/trade"],
                costCodes: ["/cost_code"],
                employees: ["/employee", "/foreman"],
                equipment: ["/modifier_active/equipment"],
                picklistItems: (user?.company_tk_modifiers ?? []).filter(m => m.standard_tk_modifier_type == null).map(m => `/modifier_active/${m.slug}`),
                projects: ["/project"],
                workShifts: ["/work_shift_id"],
                ...(getFlagEnabled("WA-9079-wage-rates")
                    ? {
                          laborRateMatches: ["/rate_match_st", "/rate_match_ot", "/rate_match_dt"],
                      }
                    : {}),
            },
            absences: {
                employees: ["/employee", "/foreman"],
                projects: [],
                companyAbsenceTypes: ["/absence_type"],
                workShifts: ["/work_shift_id"],
            },
            employeeEntries: {
                employees: ["/employee", "/foreman"],
                costCodes: ["/store/code/id"],
                employeeSchemas: ["/schema"],
                equipment: ["/store/equipment/id"],
                projects: ["/project"],
                workShifts: ["/work_shift_id"],
            },
            ewsStartStopTimes: {
                employees: ["/employee", "/foreman"],
                workShifts: ["/work_shift_id"],
                companyStartStopTypes: ["/company_start_stop_type"],
            },
            timelineEntryVersions: {
                employees: ["/employee", "/foreman"],
                projects: ["/project"],
            },
            workShifts: {
                projects: ["/projects"],
            },
        },
        initAction: () => dispatch => {
            // Load the EoD question catalog once, to populate the per-question Fancy Find categories
            if (getFlagEnabled("WA-9511-end-of-day-questions")) dispatch(loadAllEntities("eodQuestionFancyFindOptions"))
        },
        colDefs: [
            {
                ...dateColDefCreator(),
                valueSetter: workShiftDateDefaultTimeValueSetter,
                pivot: true,
            },
            timeCardColDefCreator(),
            {
                headerName: "Time Card",
                field: "/work_shift_id",
                referenceableMap: {
                    referenceableKey: "workShifts",
                    rowKey: "workShiftId",
                },
                cellRendererParams: {
                    titleFormatter: params => (params.value ? params.value : "-"),
                },
                cellRenderer: DefaultCellRenderer,
            },
            foremanColDefCreator({
                headerName: "Time Card Owner",
                moreDetail: true,
            }),
            {
                ...employeeColDefCreator({
                    moreDetail: true,
                }),
                rowGroup: true,
            },
            stringColDefCreator({
                headerName: "Classification",
                field: "/classification",
                referenceableMap: {
                    referenceableKey: "employees",
                    rowKey: "employee",
                },
            }),
            projectColDefCreator({ moreDetail: true }),
            {
                ...costCodeColDefCreator({ moreDetail: true }),
                rowGroup: true,
            },
            {
                headerName: "Trade",
                field: "/trade",
                referenceableMap: {
                    referenceableKey: "employees",
                    rowKey: "employee",
                },
                cellRendererParams: {
                    titleFormatter: params => (params.value ? params.value : "-"),
                },
                // TODO: there are other similar features in the pipeline, figure out the best way to
                // handle this as we build those
                filterColumnOf: "employee",
                cellRenderer: DefaultCellRenderer,
                editableValueGetter: params => params.context.referenceableData.employees[params.data.employee],
                filterQueryParam: "employee_trade",
            },
            timekeepingStatusColDefCreator({
                enum: getAvailableStatusValues(userRole),
            }),
            {
                headerName: "Value",
                minWidth: 107,
                // There's a minor change to the default value of the aggregation which helps
                // to determine if nothing was entered for a cell. Putting this change behind
                // a flag since it has such a broad reach
                aggFunc: "aggregateObjectEmptyDefault",
                cellRenderer: TimecardCellRenderer,
                valueGetter: pivotValueValueGetter,
            },
        ],
        gridSettings: {
            pivotHeaderHeight: 60,
            rowHeight: 80,
            pivotMode: true,
            pivotRowTotals: "after",
            autoGroupColumnDef: {
                headerName: "Employee", // might need an OtherSetting to make this based off of vars in the grid
                suppressMenu: true, // this too
                sort: "asc",
                unSortIcon: true,
                cellRendererParams: {
                    suppressCount: true,
                },
                comparator: compareGroupColumns,
                minWidth: 250,
                storeWidth: true,
            },
            defaultColDef: {
                getQuickFilterText: filterFormatter,
            },
            rowSelection: "multiple",
            // hide the group header, we don't like it
            pivotGroupHeaderHeight: 0,
            rowBuffer: 40,
        },
        otherSettings: {
            forceWeeklyPivotColumns: true,
            flexColumns: true,
            pivotColDef: {
                headerComponent: PivotHeader,
                headerComponentParams: {
                    subtitles: ["ST", "OT", "DT"],
                },
                ...customTooltip,
                comparator: compareTimekeepingEntryObjects,
                dayOfWeekHeaderName: true,
                minWidth: 107,
                cellClassRules: {
                    "period-locked": lockedPeriodPivotCellClassRule,
                    readonly: false,
                },
                editable: () => true,
                ...pivotFlex,
            },
            showLazyResources: () => getCookie("show_employee_mode") && getCookie("show_employee_mode") !== SHOW_EMPLOYEE_MODE.EMPLOYEES_WITH_TIME,
            showMainResources: () => getCookie("show_employee_mode") !== SHOW_EMPLOYEE_MODE.EMPLOYEES_WITHOUT_TIME,
            pivotRowTotalColDef: {
                minWidth: 200,
                headerComponent: PivotHeader,
                headerComponentParams: {
                    subtitles: ["All", "ST", "OT", "DT"],
                    classes: ["total-header"],
                },
                headerName: "Total",
                excludeFromFind: true,
                isTotalCell: true,
                ...pivotTotalFlex,
                ...totalCustomTooltip,
            },
            enableClipboard: true,
            skipLoadOnMount: true,
            filterEmptyWorkShifts: getFlagEnabled("WA-9660-weekly-view-empty-work-shift"),
            findConfig: {
                // The first set of keys correspond to the dropdown options next to the search bar
                employee: {
                    // We provide a display name for the dropdown option
                    dropdownDisplayName: "Employee",
                    textInputPlaceholder: "Find name or ID#",
                    // list resources which are to be searched in order to find a match
                    // on this type of object.
                    resources: [
                        {
                            // Which type of entity are we looking a?
                            sourceType: "timekeepingEntries",
                            // Which field on this entity type do we look at
                            sourceField: "/employee",
                            // Optionally, we use this field to reference another object (i.e. if the api contains
                            // only the object ID, but we also pulled that related object into the store).
                            // The section below describes which entity type we reference and which fields
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        ...(getFlagEnabled("WA-9025-CICO-fancy-find")
                            ? [
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/employee",
                                      reference: {
                                          resource: "employees",
                                          fields: employeeSearchFields,
                                      },
                                  },
                              ]
                            : []),
                        ...(getFlagEnabled("WA-8820-workers-with-no-time")
                            ? [
                                  {
                                      sourceType: "employees",
                                      sourceField: "/employee",
                                      reference: {
                                          resource: "employees",
                                          fields: employeeSearchFields,
                                      },
                                  },
                              ]
                            : []),
                    ],
                },
                // The first set of keys correspond to the dropdown options next to the search bar
                workshift: {
                    // We provide a display name for the dropdown option
                    dropdownDisplayName: "Time Card ID",
                    textInputPlaceholder: "Find ID#",
                    // list resources which are to be searched in order to find a match
                    // on this type of object.
                    resources: [
                        {
                            // Which type of entity are we looking a?
                            sourceType: "timekeepingEntries",
                            // Which field on this entity type do we look at
                            sourceField: "/work_shift_id",
                            // Optionally, we use this field to reference another object (i.e. if the api contains
                            // only the object ID, but we also pulled that related object into the store).
                            // The section below describes which entity type we reference and which fields
                            reference: {
                                resource: "workShifts",
                                fields: ["/id"],
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/work_shift_id",
                            reference: {
                                resource: "workShifts",
                                fields: ["/id"],
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/work_shift_id",
                            reference: {
                                resource: "workShifts",
                                fields: ["/id"],
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/work_shift_id",
                            reference: {
                                resource: "workShifts",
                                fields: ["/id"],
                            },
                        },
                    ],
                },
                foreman: {
                    dropdownDisplayName: "Time Card Owner",
                    textInputPlaceholder: "Find name or ID#",
                    resources: [
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: employeeSearchFields,
                            },
                        },
                        ...(getFlagEnabled("WA-9025-CICO-fancy-find")
                            ? [
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/foreman",
                                      reference: {
                                          resource: "employees",
                                          fields: employeeSearchFields,
                                      },
                                  },
                              ]
                            : []),
                    ],
                },
                project: {
                    dropdownDisplayName: "Project",
                    textInputPlaceholder: "Find name or job#",
                    resources: [
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/project",
                            reference: {
                                resource: "projects",
                                fields: ["/job_number", "/name"],
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/project",
                            reference: {
                                resource: "projects",
                                fields: ["/job_number", "/name"],
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/project",
                            reference: {
                                resource: "projects",
                                fields: ["/job_number", "/name"],
                            },
                        },
                        ...(getFlagEnabled("WA-9025-CICO-fancy-find")
                            ? [
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/project",
                                      reference: {
                                          resource: "projects",
                                          fields: ["/job_number", "/name"],
                                      },
                                  },
                              ]
                            : []),
                    ],
                },
                classification: {
                    dropdownDisplayName: "Classification",
                    textInputPlaceholder: "Find a classification",
                    resources: [
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/modifier_active/classification",
                            reference: {
                                resource: "companyClassifications",
                                fields: ["/code", "/name"],
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/classification"],
                            },
                        },
                        ...(getFlagEnabled("WA-9025-CICO-fancy-find")
                            ? [
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/employee",
                                      reference: {
                                          resource: "employees",
                                          fields: ["/classification"],
                                      },
                                  },
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/foreman",
                                      reference: {
                                          resource: "employees",
                                          fields: ["/classification"],
                                      },
                                  },
                              ]
                            : []),
                    ],
                },
                trade: {
                    dropdownDisplayName: "Trade",
                    textInputPlaceholder: "Find a trade",
                    resources: [
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/modifier_active/trade",
                            reference: {
                                resource: "companyTrades",
                                fields: ["/name", "/code"],
                            },
                        },
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/employee",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/foreman",
                            reference: {
                                resource: "employees",
                                fields: ["/trade"],
                            },
                        },
                        ...(getFlagEnabled("WA-9025-CICO-fancy-find")
                            ? [
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/employee",
                                      reference: {
                                          resource: "employees",
                                          fields: ["/trade"],
                                      },
                                  },
                                  {
                                      sourceType: "timelineEntryVersions",
                                      sourceField: "/foreman",
                                      reference: {
                                          resource: "employees",
                                          fields: ["/trade"],
                                      },
                                  },
                              ]
                            : []),
                    ],
                },
                status: {
                    dropdownDisplayName: "Status",
                    textInputPlaceholder: "Find a status",
                    enum: statusesByUserRole.map(status => ({ label: status.label, value: status.name })),
                    resources: [
                        // Without the "reference" section, we only search through the sourceField
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/status",
                        },
                        {
                            sourceType: "absences",
                            sourceField: "/status",
                        },
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/status",
                        },
                        {
                            sourceType: "ewsStartStopTimes",
                            sourceField: "/status",
                        },
                    ],
                },
                costCode: {
                    dropdownDisplayName: "Cost Code",
                    textInputPlaceholder: "Find a cost code name or description",
                    resources: [
                        {
                            sourceType: "timekeepingEntries",
                            sourceField: "/cost_code",
                            reference: {
                                resource: "costCodes",
                                fields: ["/code", "/description"],
                            },
                        },
                    ],
                },
                ...(getFlagEnabled("WA-9197-pto-accruals") ? { pto: buildPTOFindConfig() } : {}),
                absence: {
                    dropdownDisplayName: "Absences",
                    textInputPlaceholder: "Any absence",
                    enumResource: "companyAbsenceTypes",
                    enumField: "/name",
                    // When the category is selected, we automatically filter
                    // out empty values
                    autoFilterEmptyValues: true,
                    resources: [
                        {
                            sourceType: "absences",
                            sourceField: "/absence_type",
                            reference: {
                                resource: "companyAbsenceTypes",
                                fields: ["/name"],
                            },
                        },
                    ],
                },
                shiftExtra: {
                    dropdownDisplayName: "Shift Extras",
                    textInputPlaceholder: "Any shift extra",
                    enumResource: "employeeSchemas",
                    enumField: "/name",
                    // When the category is selected, we automatically filter
                    // out empty values
                    autoFilterEmptyValues: true,
                    resources: [
                        {
                            sourceType: "employeeEntries",
                            sourceField: "/schema",
                            reference: {
                                resource: "employeeSchemas",
                                fields: ["/name"],
                            },
                        },
                    ],
                },
                ...(shouldShowShiftsAndBreaks
                    ? {
                          ewsStartStopTime: {
                              dropdownDisplayName: "Shifts and Breaks",
                              textInputPlaceholder: "Worker/Time Card Owner name, Shift, Break, Meal, etc.",
                              autoFilterEmptyValues: true,
                              resources: [
                                  {
                                      sourceType: "ewsStartStopTimes",
                                      sourceField: "/start_stop_type",
                                  },
                                  {
                                      sourceType: "ewsStartStopTimes",
                                      sourceField: "/foreman",
                                      reference: {
                                          resource: "employees",
                                          fields: ["/first_name", "/last_name", "/company_supplied_id"],
                                      },
                                  },
                                  {
                                      sourceType: "ewsStartStopTimes",
                                      sourceField: "/employee",
                                      reference: {
                                          resource: "employees",
                                          fields: ["/first_name", "/last_name", "/company_supplied_id"],
                                      },
                                  },
                              ],
                          },
                      }
                    : {}),
                ...(featureFlags.cost_code_level_modifiers
                    ? {
                          modifier: {
                              dropdownDisplayName: "Modifiers",
                              textInputPlaceholder: "Any modifiers...",
                              textInputDisabled: true,
                              // When the category is selected, we automatically filter
                              // out empty values
                              autoFilterEmptyValues: true,
                              resources: [
                                  {
                                      sourceType: "timekeepingEntries",
                                      sourceField: "/modifier_active",
                                  },
                              ],
                          },
                      }
                    : {}),
                ...(featureFlags.timekeeping_comments
                    ? {
                          comment: {
                              dropdownDisplayName: "Comment",
                              textInputPlaceholder: "Any comment",
                              // When the category is selected, we automatically filter
                              // out empty values
                              autoFilterEmptyValues: true,
                              resources: [
                                  {
                                      sourceType: "timekeepingEntries",
                                      sourceField: "/comment",
                                  },
                                  {
                                      sourceType: "ewsStartStopTimes",
                                      sourceField: "/comment",
                                  },
                              ],
                          },
                      }
                    : {}),
                ...textFieldFindConfigs,
                signature: buildSignatureFindConfig(),
                ...(getFlagEnabled("WA-9511-end-of-day-questions")
                    ? {
                          ...buildEodQuestionFindConfigs(eodQuestionFancyFindOptions),
                          eodIssueAnswers: buildEodIssueAnswersFindConfig(eodQuestionFancyFindOptions),
                          eodAnswerComments: buildEodCommentsFindConfig(),
                      }
                    : {}),
            },
            groupBy: groupByConfiguration,
            buttons: {
                cell: [
                    {
                        label: "Update Status ▾",
                        icon: "status",
                        action: "updateTimekeepingStatusForFocusedPivotCell",
                        // If all TK entries have Synced status, disable the action and show a tooltip on hover.
                        disabled: context => selectedCellHasAllSyncedData(context) || selectedCellUnableToTransition(context),
                        tooltip: context =>
                            selectedCellHasAllSyncedData(context)
                                ? "Entries with a Synced status may only be updated through the Public API."
                                : selectedCellUnableToTransition(context)
                                ? "No transitions available for current selection."
                                : undefined,
                    },
                    {
                        label: "Edit",
                        icon: "edit",
                        action: "editFocusedPivotCell",
                    },
                    {
                        label: "Copy",
                        icon: "copy",
                        action: "copyFocusedPivotCell",
                    },
                    {
                        label: "Paste",
                        icon: "paste",
                        action: "pasteIntoFocusedPivotCell",
                        disabled: context => !canPasteIntoCell(context).canPaste,
                        tooltip: context => canPasteIntoCell(context).tooltip,
                    },
                    {
                        label: "Delete",
                        icon: "delete",
                        action: "deleteFocusedPivotCell",
                    },
                    {
                        label: "History",
                        icon: "history",
                        action: "openHistoryModalForFocusedPivotCell",
                    },
                    {
                        label: "Duplicate",
                        icon: "copy",
                        action: "duplicateWeeklyViewCell",
                        disabled: context =>
                            // There was a scenario where the list of entries was undefined,
                            // so we need to handle that by adding a null check.
                            Object.values(context.selectedCellData).reduce((acc, el) => acc + el?.length || 0, 0) === 0,
                        tooltip: context =>
                            Object.values(context.selectedCellData).reduce((acc, el) => acc + el?.length || 0, 0) === 0
                                ? "Selected cell has no data to duplicate"
                                : "Duplicate selected cell's data to a new date",
                    },
                    ...(!featureFlags.disable_web_signature_flow
                        ? [
                              {
                                  label: "Sign",
                                  icon: "signature",
                                  action: "getSignatureOptions",
                                  disabled: !singleEmployeeIsSelected,
                                  tooltip: getSignatureTooltip,
                              },
                          ]
                        : []),
                    ...(featureFlags.text_for_signature && !featureFlags.disable_web_signature_flow
                        ? [
                              {
                                  label: "Request Signature",
                                  icon: "share",
                                  action: "getSignatureRequestOptions",
                                  tooltip: getSignatureRequestTooltip,
                              },
                          ]
                        : []),
                ],
                row: [],
                table: [
                    {
                        label: "Expand/Collapse All",
                        action: "toggleRowExpansion",
                        icon: "expand",
                    },
                    {
                        label: "Group By",
                        action: "groupBy",
                        icon: "group",
                    },
                    {
                        label: "Find",
                        action: "toggleFancySearchBar",
                        icon: "find",
                        customButtonType: "find",
                        activeClass: "fancySearchBarEnabled",
                    },
                    ...(getFlagEnabled("WA-9336-weekly-copy")
                        ? [
                              {
                                  label: "Copy Week",
                                  icon: "copy",
                                  action: "openCopyWeekModal",
                                  disabled: context => getCopyWeekButtonState(context).disabled,
                                  tooltip: context => getCopyWeekButtonState(context).tooltip,
                              },
                          ]
                        : []),
                    {
                        separator: true,
                    },
                    {
                        label: "Create Time Card",
                        action: "openCreateTimeCardModal",
                        icon: "add",
                    },
                    {
                        label: "Add Entry",
                        action: "openAddTimekeepingEntryModal",
                        icon: "add",
                    },
                    {
                        label: "Update All Statuses ▾",
                        icon: "status",
                        action: "updateTimekeepingStatusForTable",
                        disabled: context =>
                            selectedCellUnableToTransition({
                                selectedCellData: filterData(sourceData.sourceData, context),
                                currentUser: user,
                            }),
                        tooltip: context =>
                            selectedCellUnableToTransition({
                                selectedCellData: filterData(sourceData.sourceData, context),
                                currentUser: user,
                            })
                                ? "No transitions available for current selection."
                                : undefined,
                    },
                    {
                        separator: true,
                    },
                    ...(getFlagEnabled("WA-8640-fancy-csv-exporter")
                        ? [
                              {
                                  label: "Export ▾",
                                  icon: "export",
                                  export: true,
                              },
                          ]
                        : [
                              {
                                  label: "Export",
                                  icon: "export",
                                  action: "navigateTo",
                                  args: {
                                      url: "/rhumbix/export/csv/",
                                  },
                              },
                          ]),
                    ...[
                        {
                            label: "Exceptions ▾",
                            icon: "exception",
                            showBadge: context => {
                                const workRuleOverride = context?.exceptions?.workRulesException?.workRuleOverride
                                return workRuleOverride && workRuleOverride.length > 0 ? createElement(OverrideBadge, null, "\u00a0") : null
                            },
                            args: {
                                extraArgs: {
                                    options: [
                                        {
                                            label: "Exceptions",
                                            action: allExceptionsToggle,
                                            exceptionParams: featureFlags.cico ? ["cicoDiffException", "workRulesException"] : ["workRulesException"],
                                            hasGear: false,
                                        },
                                        workRulesException,
                                        ...(featureFlags.cico ? [cicoException] : []),
                                    ],
                                    filterToggle: true,
                                    exceptionList: true,
                                    doTkSummaryFetch: true,
                                    doEodAnswersFetch: getFlagEnabled("WA-9511-end-of-day-questions"),
                                },
                            },
                            checklist: true,
                        },
                    ],
                    ...(getFlagEnabled("WA-8820-workers-with-no-time")
                        ? [
                              {
                                  label: `${SHOW_EMPLOYEE_MODE_LABELS[getCookie("show_employee_mode") ?? SHOW_EMPLOYEE_MODE.EMPLOYEES_WITH_TIME]} ▾`,
                                  tooltip: "Toggle between showing employees with timekeeping data, all employees, " + "and employees missing timekeeping data",
                                  action: "getEmployeeModeChangeHandlers",
                              },
                          ]
                        : []),
                ],
            },
            editablePivotSourceModal: {
                includeTablesForAllSchemas: true,
                tableList: [
                    getTimekeepingEntryTable(featureFlags, currentProject, user, currentProjectIds, sourceData),
                    getAbsenceTable(featureFlags, currentProject, user),
                    ...(shouldShowShiftsAndBreaks ? [getShiftsAndBreaksTable(featureFlags, currentProject, user)] : []),
                    ...(getFlagEnabled("WA-9197-pto-accruals") ? [getPTORequestsTable(featureFlags, currentProject, user)] : []),
                ],
            },
            enableSearchBar: false,
            enableFancySearchBar: true,
            exceptionFilters,
            enableAuditHistory: true,
            enableFindMatchRule: true,
        },
    }
}
